import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import {
  FileText,
  MessageSquare,
  Upload,
  BarChart3,
  Clock,
  TrendingUp,
  Plus
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const { documents, chatSessions, subscription, usage, loading, fetchUserData } = useApp();

  useEffect(() => {
    fetchUserData();
  }, []);

  const documentsUsed = usage?.documents_used ?? documents.length;
  const documentsLimit = usage?.documents_limit;
  const chatsUsedToday = usage?.chat_sessions_used ?? chatSessions.length;
  const chatsLimit = usage?.chat_sessions_limit;
  const storageUsedMb = Math.round((usage?.storage_used || 0) / 1024 / 1024);
  const storageLimitMb = subscription?.plan_type === 'FREE' ? 5 : subscription?.plan_type === 'PRO' ? 50 : 500;

  const stats = [
    {
      title: 'Documents',
      value: documentsUsed,
      limit: documentsLimit === null || documentsLimit === undefined ? '∞' : documentsLimit,
      icon: FileText,
      color: 'blue',
      link: '/documents'
    },
    {
      title: 'Chat Sessions (Today)',
      value: chatsUsedToday,
      limit: chatsLimit === null || chatsLimit === undefined ? '∞' : chatsLimit,
      icon: MessageSquare,
      color: 'purple',
      link: '/chat'
    },
    {
      title: 'Storage Used',
      value: `${storageUsedMb}MB`,
      limit: `${storageLimitMb}MB`,
      icon: BarChart3,
      color: 'green',
      link: '/subscription'
    }
  ];

  const recentDocuments = documents.slice(0, 5);
  const recentSessions = chatSessions.slice(0, 5);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome back, {user?.full_name?.split(' ')[0] || 'there'}!
        </h1>
        <p className="text-gray-600 mt-2">
          Here's what's happening with your documents and AI conversations.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {stats.map((stat, index) => (
          <Link
            key={index}
            to={stat.link}
            className="card hover:shadow-md transition-shadow duration-300 group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {stat.value}
                  <span className="text-sm font-normal text-gray-500 ml-1">
                    / {stat.limit}
                  </span>
                </p>
              </div>
              <div className={`p-3 rounded-lg bg-${stat.color}-100 group-hover:bg-${stat.color}-200 transition-colors duration-300`}>
                <stat.icon className={`h-6 w-6 text-${stat.color}-600`} />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Link
          to="/upload"
          className="card hover:shadow-md transition-all duration-300 group border-2 border-dashed border-gray-300 hover:border-purple-400"
        >
          <div className="text-center py-8">
            <div className="p-4 bg-purple-100 rounded-full inline-block mb-4 group-hover:bg-purple-200 transition-colors duration-300">
              <Upload className="h-8 w-8 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Documents</h3>
            <p className="text-gray-600">
              Add new documents to analyze and chat with
            </p>
          </div>
        </Link>

        <Link
          to="/chat"
          className="card hover:shadow-md transition-all duration-300 group border-2 border-dashed border-gray-300 hover:border-blue-400"
        >
          <div className="text-center py-8">
            <div className="p-4 bg-blue-100 rounded-full inline-block mb-4 group-hover:bg-blue-200 transition-colors duration-300">
              <MessageSquare className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Start Conversation</h3>
            <p className="text-gray-600">
              Begin a new AI-powered chat session
            </p>
          </div>
        </Link>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Documents */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Recent Documents</h2>
            <Link to="/documents" className="text-purple-600 hover:text-purple-700 text-sm font-medium">
              View all
            </Link>
          </div>
          
          {recentDocuments.length > 0 ? (
            <div className="space-y-4">
              {recentDocuments.map((doc) => (
                <div key={doc.id} className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors duration-200">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {doc.filename}
                    </p>
                    <p className="text-xs text-gray-500">
                      Uploaded {new Date(doc.upload_date || doc.created_at || Date.now()).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No documents uploaded yet</p>
              <Link to="/upload" className="text-purple-600 hover:text-purple-700 text-sm font-medium">
                Upload your first document
              </Link>
            </div>
          )}
        </div>

        {/* Recent Chat Sessions */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Recent Chats</h2>
            <Link to="/chat" className="text-purple-600 hover:text-purple-700 text-sm font-medium">
              View all
            </Link>
          </div>
          
          {recentSessions.length > 0 ? (
            <div className="space-y-4">
              {recentSessions.map((session) => (
                <div key={session.id} className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors duration-200">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <MessageSquare className="h-4 w-4 text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {session.title || 'Untitled Chat'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(session.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No chat sessions yet</p>
              <Link to="/chat" className="text-purple-600 hover:text-purple-700 text-sm font-medium">
                Start your first conversation
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Subscription Notice */}
      {subscription?.plan_type === 'FREE' && (
        <div className="mt-8 card bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-purple-900">Upgrade to Pro</h3>
              <p className="text-purple-700 mt-1">
                Get more documents, larger files, and advanced features
              </p>
            </div>
            <Link to="/subscription" className="btn-primary">
              Upgrade Now
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
